import { getApiHost } from '@/appSettings';
import { useLoginStore } from '@/login/stores/useLoginStore';
import { RestManager } from '@/newApi/services/rest/RestManager';
import { currentInvolveScenario, getMockInvolveDashboard } from '@/mocks/mockInvolveDashboard';
import type { EmploymentSalaryType } from '@sysarb/sysarb-types-v2/general/enums';
import type { EmploymentDashboardData } from '@sysarb/sysarb-types-v2/services/v1/employment/involvePortal';
import type { EmploymentDashboardV2Response } from '@sysarb/sysarb-types-v2/services/v1/employment/involvePortal';
import type { QueryOptions } from '@tanstack/vue-query';
import { queryOptions } from '@tanstack/vue-query';

const baseRegistryService = new RestManager(`https://${getApiHost()}/base-registry-service`);

const mock = {
    involveDashboard: false,
};

type InvolveDashboardOptions = {
    queries: {
        getInvolveDashboard: (employmentId: string | null, salaryType: EmploymentSalaryType) => QueryOptions<EmploymentDashboardData | null, Error>;
        /** Applies the employee's country or legal entity settings. 'no-settings' when none of them is saved yet. Custom dashboards stay on v1. */
        getInvolveDashboardV2: (employmentId: string | null, salaryType: EmploymentSalaryType) => QueryOptions<EmploymentDashboardV2Response | null, Error>;
    };
};

export const involveDashboard: InvolveDashboardOptions = {
    queries: {
        getInvolveDashboard: (employmentId: string | null, salaryType: EmploymentSalaryType): QueryOptions<EmploymentDashboardData | null, Error> => {
            const { instance } = useLoginStore();
            // The backend can't read the payRangesV2 flag, so it is told which pay range model to use:
            // v1 is the pay bands, v2 the pay range the employment is assigned to.

            return queryOptions<EmploymentDashboardData | null, Error>({
                queryKey: ['section-manager', 'involve-dashboard', instance, employmentId, salaryType, mock.involveDashboard ? currentInvolveScenario : null],
                queryFn: async (): Promise<EmploymentDashboardData | null> => {
                    if (mock.involveDashboard) return getMockInvolveDashboard();
                    const url = `v1/${instance}/involve/employment/${employmentId}/dashboard?salaryType=${salaryType}&payRangeVersion=v2`;
                    return baseRegistryService.get<EmploymentDashboardData>(url);
                },
                enabled: () => employmentId != null,
            });
        },
        getInvolveDashboardV2: (employmentId: string | null, salaryType: EmploymentSalaryType): QueryOptions<EmploymentDashboardV2Response | null, Error> => {
            const { instance } = useLoginStore();

            return queryOptions<EmploymentDashboardV2Response | null, Error>({
                queryKey: ['section-manager', 'involve-dashboard-v2', instance, employmentId, salaryType],
                queryFn: async (): Promise<EmploymentDashboardV2Response | null> => {
                    const url = `v2/${instance}/involve/employment/${employmentId}/dashboard?salaryType=${salaryType}&payRangeVersion=v2`;
                    return baseRegistryService.get<EmploymentDashboardV2Response>(url);
                },
                enabled: () => employmentId != null,
            });
        },
    },
};
